import React, { useEffect, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Divider,
  styled,
  Paper,
  FormControlLabel,
  Switch,
  Checkbox,
  Button,
  MenuItem,
} from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NotesIcon from "@mui/icons-material/Notes";
import DescriptionIcon from "@mui/icons-material/Description";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ScaleIcon from "@mui/icons-material/Scale";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCities,
  fetchCountries,
  fetchStates,
} from "../../../../features/buyer/buyerSlice";

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)",
  borderRadius: "20px",
  border: "1px solid rgba(0, 0, 0, 0.06)",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
  padding: "48px",
  maxWidth: "1200px",
  margin: "0 auto",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    boxShadow: "0 15px 50px rgba(0, 0, 0, 0.12)",
  },
  [theme.breakpoints.down("md")]: {
    padding: "24px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#ffffff",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    border: "2px solid rgba(0, 0, 0, 0.06)",
    fontSize: "15px",
    fontWeight: "500",
    color: "#1a1a1a",
    "& fieldset": {
      borderColor: "rgba(0, 0, 0, 0.06)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(25, 118, 210, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "primary.main",
      borderWidth: "2px",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.1)",
      backgroundColor: "#ffffff",
    },
  },
  "& .MuiInputBase-input": {
    padding: "16px 16px",
    fontFamily: "'Inter', sans-serif",
    fontWeight: "500",
    "&::placeholder": {
      opacity: "0.6",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Inter', sans-serif",
    fontWeight: "600",
    color: "#4a5568",
    fontSize: "14px",
    transform: "translate(14px, -12px) scale(0.75)",
    backgroundColor: "#ffffff",
    paddingLeft: "6px",
    paddingRight: "6px",
    marginLeft: "-3px",
    "&.Mui-focused": {
      color: "primary.main",
    },
  },
  "& .MuiFormHelperText-root": {
    fontFamily: "'Inter', sans-serif",
    fontSize: "13px",
    marginLeft: "0",
    color: "#718096",
    marginTop: "8px",
  },
}));

const SectionTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "24px",
  marginTop: "32px",
  paddingTop: "24px",
  borderTop: "2px solid rgba(0, 0, 0, 0.05)",
  "&:first-of-type": {
    marginTop: "0",
    borderTop: "none",
    paddingTop: "0",
  },
});

const IconWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  borderRadius: "8px",
  backgroundColor: "rgba(25, 118, 210, 0.1)",
  color: "primary.main",
  "& svg": {
    width: "16px",
    height: "16px",
  },
});

const SectionLabel = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontWeight: "700",
  fontSize: "14px",
  textTransform: "uppercase",
  letterSpacing: "1px",
  color: "primary.main",
});

const HeadingBox = styled(Box)({
  marginBottom: "40px",
  paddingBottom: "32px",
  borderBottom: "2px solid rgba(0, 0, 0, 0.05)",
});

const FormGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "28px",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: "20px",
  },
}));

const DocumentGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "28px",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: "20px",
  },
}));

const FullWidthGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "28px",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: "20px",
  },
}));

const FourColGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "28px",
  marginBottom: "20px",
  [theme.breakpoints.down("lg")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const AssistanceBox = styled(Box)(({ theme }) => ({
  padding: "20px",
  borderRadius: "12px",
  background:
    "linear-gradient(135deg, rgba(25, 118, 210, 0.04) 0%, rgba(25, 118, 210, 0.02) 100%)",
  border: "1px solid rgba(25, 118, 210, 0.1)",
  marginBottom: "24px",
  marginTop: "24px",
  display: "flex",
  // alignItems: "center",
  flexDirection: "column",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    borderColor: "rgba(25, 118, 210, 0.2)",
    backgroundColor: "rgba(25, 118, 210, 0.06)",
  },
}));

const NotesBox = styled(Box)({
  marginBottom: "8px",
});

const headingStyles = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontWeight: "800",
  fontSize: "30px",
  color: "#0a0e27",
  marginBottom: "12px",
  background: "linear-gradient(135deg, #0a0e27 0%, #1976d2 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
});

const subheadingStyles = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: "15px",
  fontWeight: "500",
  color: "#718096",
  lineHeight: "1.6",
});

export function ThirdPartyShippingForm({ control, errors, setValue, trigger }) {
  const dispatch = useDispatch();

  const { countries, pickup, billing, delivery, loading } = useSelector(
    (state) => state.buyer.addressSelector,
  );

  const documents = [
    { name: "deliveryNote", label: "Delivery Note", icon: <ReceiptIcon /> },
    { name: "packingList", label: "Packing List", icon: <ListAltIcon /> },
    { name: "weightSlip", label: "Weight Slip", icon: <ScaleIcon /> },
    { name: "invoice", label: "Invoice", icon: <DescriptionIcon /> },
    {
      name: "consignmentNote",
      label: "Consignment Note",
      icon: <LocalShippingIcon />,
    },
    { name: "manifest", label: "Manifest", icon: <DashboardIcon /> },
  ];

  const [transport, setTransport] = useState({
    documents: {
      deliveryNote: [],
      packingList: [],
      weightSlip: [],
      invoice: [],
      consignmentNote: [],
      manifest: [],
    },
  });

  const handleFileUpload = (type) => (file) => {
    setTransport((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        [type]: [file],
      },
    }));
  };

  const pickupCountry = useWatch({
    control,
    name: "pickupAddress.country",
  });

  const pickupState = useWatch({
    control,
    name: "pickupAddress.state",
  });

  const deliveryCountry = useWatch({
    control,
    name: "deliveryAddress.country",
  });

  const deliveryState = useWatch({
    control,
    name: "deliveryAddress.state",
  });

  const billingCountry = useWatch({
    control,
    name: "billingAddress.country",
  });

  const billingState = useWatch({
    control,
    name: "billingAddress.state",
  });

  const today = new Date().toISOString().split("T")[0];

  const pickupStartDate = useWatch({
    control,
    name: "pickupAddress.startDate",
  });

  const deliveryStartDate = useWatch({
    control,
    name: "deliveryAddress.startDate",
  });

  // Load countries once
  useEffect(() => {
    dispatch(fetchCountries());
  }, [dispatch]);
  // console.log("Countries:", countries);

  // Country → States
  useEffect(() => {
    if (pickupCountry) {
      dispatch(fetchStates({ countryId: pickupCountry, type: "pickup" }));
      setValue("pickupAddress.state", "");
      setValue("pickupAddress.city", "");
    }
  }, [pickupCountry, dispatch, setValue]);
  // console.log("States:", pickup.states);

  // State → Cities
  useEffect(() => {
    if (pickupState) {
      dispatch(fetchCities({ stateId: pickupState, type: "pickup" }));
      setValue("pickupAddress.city", "");
    }
  }, [pickupState, dispatch, setValue]);

  // Country → States
  useEffect(() => {
    if (deliveryCountry) {
      dispatch(fetchStates({ countryId: deliveryCountry, type: "delivery" }));
      setValue("deliveryAddress.state", "");
      setValue("deliveryAddress.city", "");
    }
  }, [deliveryCountry, dispatch, setValue]);
  console.log("States:", delivery.states);

  // State → Cities
  useEffect(() => {
    if (deliveryState) {
      dispatch(fetchCities({ stateId: deliveryState, type: "delivery" }));
      setValue("deliveryAddress.city", "");
    }
  }, [deliveryState, dispatch, setValue]);

  useEffect(() => {
    if (billingCountry) {
      dispatch(fetchStates({ countryId: billingCountry, type: "billing" }));

      setValue("billingAddress.state", "");
      setValue("billingAddress.city", "");
    }
  }, [billingCountry, dispatch, setValue]);

  useEffect(() => {
    if (billingState) {
      dispatch(fetchCities({ stateId: billingState, type: "billing" }));
      setValue("billingAddress.city", "");
    }
  }, [billingState, dispatch, setValue]);

  return (
    <>
      {/* PICKUP ADDRESS */}
      <StyledPaper elevation={0}>
        <HeadingBox>
          <Typography
            variant="h4"
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: "800",
              fontSize: "30px",
              color: "#0a0e27",
              marginBottom: "12px",
              background: "linear-gradient(135deg, #0a0e27 0%, #1976d2 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Pickup Address
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "15px",
              fontWeight: "500",
              color: "#718096",
              lineHeight: "1.6",
            }}
          >
            Enter your pickup location details to get started
          </Typography>
        </HeadingBox>

        {/* PICKUP ADDRESS */}

        <SectionTitle>
          <IconWrapper>
            <PersonIcon />
          </IconWrapper>
          <SectionLabel>Contact Information</SectionLabel>
        </SectionTitle>

        <FormGrid>
          <Controller
            name="pickupAddress.firstName"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="First Name *"
                size="small"
                error={!!errors?.pickupAddress?.firstName}
                helperText={errors?.pickupAddress?.firstName?.message}
              />
            )}
          />

          <Controller
            name="pickupAddress.lastName"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Last Name *"
                size="small"
                error={!!errors?.pickupAddress?.lastName}
                helperText={errors?.pickupAddress?.lastName?.message}
              />
            )}
          />

          <Controller
            name="pickupAddress.phone"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Phone *"
                size="small"
                error={!!errors?.pickupAddress?.phone}
                helperText={errors?.pickupAddress?.phone?.message}
              />
            )}
          />

          <Controller
            name="pickupAddress.companyName"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Company Name"
                size="small"
                error={!!errors?.pickupAddress?.companyName}
                helperText={errors?.pickupAddress?.companyName?.message}
              />
            )}
          />
        </FormGrid>

        <SectionTitle>
          <IconWrapper>
            <LocationOnIcon />
          </IconWrapper>
          <SectionLabel>Location Details</SectionLabel>
        </SectionTitle>
        <FormGrid>
          <Controller
            name="pickupAddress.country"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                select
                fullWidth
                label="Country *"
                size="small"
                error={!!errors?.pickupAddress?.country}
                helperText={errors?.pickupAddress?.country?.message}
              >
                <MenuItem value="">Select country</MenuItem>
                {countries.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </StyledTextField>
            )}
          />

          <Controller
            name="pickupAddress.state"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                select
                fullWidth
                label="State *"
                size="small"
                error={!!errors?.pickupAddress?.state}
                helperText={errors?.pickupAddress?.state?.message}
              >
                <MenuItem value="">Select state</MenuItem>
                {pickup.states.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </StyledTextField>
            )}
          />

          <Controller
            name="pickupAddress.city"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                select
                fullWidth
                label="City *"
                size="small"
                error={!!errors?.pickupAddress?.city}
                helperText={errors?.pickupAddress?.city?.message}
              >
                <MenuItem value="">Select city</MenuItem>
                {pickup.cities.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </StyledTextField>
            )}
          />

          <Controller
            name="pickupAddress.postcode"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Postcode *"
                size="small"
                error={!!errors?.pickupAddress?.postcode}
                helperText={errors?.pickupAddress?.postcode?.message}
              />
            )}
          />
        </FormGrid>

        <SectionTitle>
          <IconWrapper>
            <HomeIcon />
          </IconWrapper>
          <SectionLabel>Address</SectionLabel>
        </SectionTitle>
        <FullWidthGrid>
          <Controller
            name="pickupAddress.addressLine1"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Address Line 1 *"
                size="small"
                error={!!errors?.pickupAddress?.addressLine1}
                helperText={errors?.pickupAddress?.addressLine1?.message}
              />
            )}
          />

          <Controller
            name="pickupAddress.addressLine2"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Address Line 2"
                size="small"
                error={!!errors?.pickupAddress?.addressLine2}
                helperText={errors?.pickupAddress?.addressLine2?.message}
              />
            )}
          />
        </FullWidthGrid>

        <SectionTitle>
          <IconWrapper>
            <LocalShippingIcon />
          </IconWrapper>
          <SectionLabel>Logistics & Equipment</SectionLabel>
        </SectionTitle>
        <FormGrid>
          <Controller
            name="pickupAddress.locationType"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                select
                fullWidth
                label="Location Type"
                SelectProps={{ native: true }}
                size="small"
                error={!!errors?.pickupAddress?.locationType}
                helperText={errors?.pickupAddress?.locationType?.message}
              >
                <option value="">Select location type</option>
                <option value="warehouse">Warehouse</option>
                <option value="retail">Retail Store</option>
                <option value="office">Office</option>
                <option value="residential">Residential</option>
              </StyledTextField>
            )}
          />
          <Controller
            name="pickupAddress.forkliftAvailability"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value === 1}
                    onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                  />
                }
                label="Forklift Available"
                error={!!errors?.pickupAddress?.forklifAvailable}
                helperText={errors?.pickupAddress?.forklifAvailable?.message}
              />
            )}
          />
        </FormGrid>
        <AssistanceBox>
          <Controller
            name="pickupAddress.noAssistanceAtPickup" // Keep the name for react-hook-form
            control={control}
            render={({ field }) => (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value === 1} // Ensure it's using the correct value
                      onChange={(e) => {
                        field.onChange(e.target.checked ? 1 : 0); // Handle onChange to update the state
                      }}
                      sx={{
                        "& .MuiSwitch-switchBase": {
                          color: "#cbd5e0",
                          "&.Mui-checked": {
                            color: "primary.main",
                            "& + .MuiSwitch-track": {
                              backgroundColor: "primary.main",
                            },
                          },
                        },
                        "& .MuiSwitch-track": {
                          backgroundColor: "#cbd5e0",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography fontWeight={600}>
                      {field.value
                        ? "Assistance Available"
                        : "No Assistance Available"}
                    </Typography>
                  }
                />

                {/* Conditionally render the Special Assistance Notes field */}
                {field.value === 1 && (
                  <>
                    <SectionTitle>
                      <IconWrapper>
                        <NotesIcon />
                      </IconWrapper>
                      <SectionLabel>Special Assistance Notes</SectionLabel>
                    </SectionTitle>
                    <Controller
                      name="pickupAddress.specialAssistanceNotes"
                      control={control}
                      shouldUnregister
                      render={({ field: notesField }) => (
                        <StyledTextField
                          {...notesField}
                          fullWidth
                          multiline
                          rows={7}
                          label="Special Assistance Notes (Optional)"
                          size="small"
                          error={!!errors?.pickupAddress?.specialNotes}
                          helperText={
                            errors?.pickupAddress?.specialNotes?.message
                          }
                        />
                      )}
                    />
                  </>
                )}
              </>
            )}
          />
        </AssistanceBox>

        <SectionTitle>
          <IconWrapper>
            <DescriptionIcon />
          </IconWrapper>
          <SectionLabel>Pickup Time Slot</SectionLabel>
        </SectionTitle>
        <FormGrid>
          <Controller
            name="pickupAddress.startDate"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                size="small"
                inputProps={{
                  min: today,
                }}
                error={!!errors?.pickupAddress?.startDate}
                helperText={errors?.pickupAddress?.startDate?.message}
              />
            )}
          />

          <Controller
            name="pickupAddress.endDate"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="End Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                size="small"
                inputProps={{
                  min: pickupStartDate || today,
                }}
                error={!!errors?.pickupAddress?.endDate}
                helperText={errors?.pickupAddress?.endDate?.message}
              />
            )}
          />

          <Controller
            name="pickupAddress.pickupTimeSlot"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                select
                fullWidth
                label="Pickup Time Slot"
                SelectProps={{ native: true }}
                size="small"
                error={!!errors?.pickupAddress?.timeSlot}
                helperText={errors?.pickupAddress?.timeSlot?.message}
              >
                <option value="">Select time slot</option>
                <option value="08:00-10:00">08:00 AM - 10:00 AM</option>
                <option value="10:00-12:00">10:00 AM - 12:00 PM</option>
                <option value="12:00-14:00">12:00 PM - 2:00 PM</option>
                <option value="14:00-16:00">2:00 PM - 4:00 PM</option>
                <option value="16:00-18:00">4:00 PM - 6:00 PM</option>
              </StyledTextField>
            )}
          />
        </FormGrid>

        <SectionTitle>
          <IconWrapper>
            <NotesIcon />
          </IconWrapper>
          <SectionLabel>Pickup Notes</SectionLabel>
        </SectionTitle>
        <NotesBox>
          <Box
            sx={{
              padding: "16px",
              borderRadius: "12px",
              backgroundColor: "rgba(255,193,7,0.08)",
              border: "1px solid rgba(255,193,7,0.3)",
              display: "flex",
              gap: "12px",
              alignItems: "flex-start",
              mb: 5,
            }}
          >
            {/* ICON */}
            <Box
              sx={{
                width: "28px",
                height: "28px",
                minWidth: "28px",
                borderRadius: "6px",
                backgroundColor: "rgba(255,193,7,0.25)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: "2px",
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 20, color: "#b7791f" }} />
            </Box>

            {/* TEXT CONTENT */}
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "15px",
                  mb: 0.5,
                  color: "#8a6d3b",
                }}
              >
                Pickup Notes — What to include
              </Typography>

              {/* 2-COLUMN LIST */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "1fr 1fr 1fr",
                  },
                  rowGap: "8px",
                  columnGap: "16px",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Gate access instructions
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Loading timings or restrictions
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Safety gear requirements
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Required documents or ID
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Instructions for fragile/heavy cargo
                </Typography>
              </Box>
            </Box>
          </Box>

          <Controller
            name="pickupAddress.pickupNotes"
            control={control}
            render={({ field: notesField }) => (
              <StyledTextField
                {...notesField}
                fullWidth
                multiline
                rows={7}
                label="Pickup Notes (Important) *"
                size="small"
                error={!!errors?.pickupAddress?.pickupNotes}
                helperText={errors?.pickupAddress?.pickupNotes?.message}
              />
            )}
          />
        </NotesBox>

        <SectionTitle>
          <IconWrapper>
            <NotesIcon />
          </IconWrapper>
          <SectionLabel>Pickup Images/Documents</SectionLabel>
        </SectionTitle>
        <FullWidthGrid
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <Box
            sx={{
              border: "2px dashed #e5e7eb",
              borderRadius: 1,
              p: 2,
              textAlign: "center",
              backgroundColor: "#f9fafb",
            }}
          >
            <CloudUpload sx={{ fontSize: 32, color: "#9ca3af", mb: 1 }} />
            <Typography variant="body2">
              Drag files or click to upload
            </Typography>
          </Box>

          <SectionLabel>Documents Upload</SectionLabel>
          <Box
            sx={{
              border: "2px dashed #e5e7eb",
              borderRadius: 1,
              p: 2,
              textAlign: "center",
              backgroundColor: "#f9fafb",
            }}
          >
            <CloudUpload sx={{ fontSize: 32, color: "#9ca3af", mb: 1 }} />
            <Typography variant="body2">
              Drag files or click to upload
            </Typography>
          </Box>
        </FullWidthGrid>
      </StyledPaper>

      {/* DELIVERY ADDRESS */}
      <StyledPaper elevation={0} style={{ marginTop: 10 }}>
        <HeadingBox>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "800",
              fontSize: "30px",
              background: "linear-gradient(135deg, #0a0e27 0%, #1976d2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              marginBottom: "12px",
            }}
          >
            Delivery Address
          </Typography>

          <Typography
            sx={{ color: "#718096", fontSize: "15px", fontWeight: 500 }}
          >
            Provide delivery location details for drop-off
          </Typography>
        </HeadingBox>

        {/* CONTACT */}
        <SectionTitle>
          <IconWrapper>
            <PersonIcon />
          </IconWrapper>
          <SectionLabel>Contact Information</SectionLabel>
        </SectionTitle>

        <FormGrid>
          <Controller
            name="deliveryAddress.firstName"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="First Name *"
                size="small"
                error={!!errors?.deliveryAddress?.firstName}
                helperText={errors?.deliveryAddress?.firstName?.message}
              />
            )}
          />
          <Controller
            name="deliveryAddress.lastName"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Last Name *"
                size="small"
                error={!!errors?.deliveryAddress?.lastName}
                helperText={errors?.deliveryAddress?.lastName?.message}
              />
            )}
          />
          <Controller
            name="deliveryAddress.phone"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Phone *"
                size="small"
                error={!!errors?.deliveryAddress?.phone}
                helperText={errors?.deliveryAddress?.phone?.message}
              />
            )}
          />
          <Controller
            name="deliveryAddress.companyName"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Company Name"
                size="small"
                error={!!errors?.deliveryAddress?.companyName}
                helperText={errors?.deliveryAddress?.companyName?.message}
              />
            )}
          />
          <Controller
            name="deliveryAddress.taxId"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="ABN/ACN/VAT/Tax ID *"
                size="small"
                error={!!errors?.deliveryAddress?.taxId}
                helperText={errors?.deliveryAddress?.taxId?.message}
              />
            )}
          />
        </FormGrid>

        {/* LOCATION */}
        <SectionTitle>
          <IconWrapper>
            <LocationOnIcon />
          </IconWrapper>
          <SectionLabel>Location Details</SectionLabel>
        </SectionTitle>

        <FormGrid>
          <Controller
            name="deliveryAddress.country"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                select
                fullWidth
                label="Country *"
                size="small"
                error={!!errors?.deliveryAddress?.country}
                helperText={errors?.deliveryAddress?.country?.message}
              >
                <MenuItem value="">Select country</MenuItem>
                {countries.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </StyledTextField>
            )}
          />
          <Controller
            name="deliveryAddress.state"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                select
                fullWidth
                label="State *"
                size="small"
                error={!!errors?.deliveryAddress?.state}
                helperText={errors?.deliveryAddress?.state?.message}
              >
                <MenuItem value="">Select state</MenuItem>
                {delivery.states.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </StyledTextField>
            )}
          />
          <Controller
            name="deliveryAddress.city"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                select
                fullWidth
                label="City *"
                size="small"
                error={!!errors?.deliveryAddress?.city}
                helperText={errors?.deliveryAddress?.city?.message}
              >
                <MenuItem value="">Select city</MenuItem>
                {delivery.cities.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </StyledTextField>
            )}
          />
          <Controller
            name="deliveryAddress.postcode"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Postcode *"
                size="small"
                error={!!errors?.deliveryAddress?.postcode}
                helperText={errors?.deliveryAddress?.postcode?.message}
              />
            )}
          />
        </FormGrid>

        {/* ADDRESS */}
        <SectionTitle>
          <IconWrapper>
            <HomeIcon />
          </IconWrapper>
          <SectionLabel>Address</SectionLabel>
        </SectionTitle>

        <FullWidthGrid>
          <Controller
            name="deliveryAddress.addressLine1"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Address Line 1 *"
                size="small"
                error={!!errors?.deliveryAddress?.addressLine1}
                helperText={errors?.deliveryAddress?.addressLine1?.message}
              />
            )}
          />
          <Controller
            name="deliveryAddress.addressLine2"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Address Line 2"
                size="small"
                error={!!errors?.billingAddress?.addressLine2}
                helperText={errors?.billingAddress?.addressLine2?.message}
              />
            )}
          />
        </FullWidthGrid>

        <SectionTitle>
          <IconWrapper>
            <LocalShippingIcon />
          </IconWrapper>
          <SectionLabel>Logistics & Equipment</SectionLabel>
        </SectionTitle>
        <FormGrid>
          <Controller
            name="deliveryAddress.locationType"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                select
                fullWidth
                label="Location Type"
                SelectProps={{ native: true }}
                size="small"
                error={!!errors?.deliveryAddress?.locationType}
                helperText={errors?.deliveryAddress?.locationType?.message}
              >
                <option value="">Select location type</option>
                <option value="warehouse">Warehouse</option>
                <option value="retail">Retail Store</option>
                <option value="office">Office</option>
                <option value="residential">Residential</option>
              </StyledTextField>
            )}
          />
          <Controller
            name="deliveryAddress.forkliftAvailability"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value === 1}
                    onChange={(e) => field.onChange(e.target.checked ? 1 : 0)}
                  />
                }
                label="Forklift Available"
                error={!!errors?.deliveryAddress?.forklifAvailable}
                helperText={errors?.deliveryAddress?.forklifAvailable?.message}
              />
            )}
          />
        </FormGrid>
        <AssistanceBox>
          <Controller
            name="deliveryAddress.noAssistanceAtDelivery" // Keep the name for react-hook-form
            control={control}
            render={({ field }) => (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value === 1} // Ensure it's using the correct value
                      onChange={(e) => {
                        field.onChange(e.target.checked ? 1 : 0); // Handle onChange to update the state
                      }}
                      sx={{
                        "& .MuiSwitch-switchBase": {
                          color: "#cbd5e0",
                          "&.Mui-checked": {
                            color: "primary.main",
                            "& + .MuiSwitch-track": {
                              backgroundColor: "primary.main",
                            },
                          },
                        },
                        "& .MuiSwitch-track": {
                          backgroundColor: "#cbd5e0",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography fontWeight={600}>
                      {field.value === 1
                        ? "Assistance Available"
                        : "No Assistance Available"}
                    </Typography>
                  }
                />

                {/* Conditionally render the Special Assistance Notes field */}
                {field.value === 1 && (
                  <>
                    <SectionTitle>
                      <IconWrapper>
                        <NotesIcon />
                      </IconWrapper>
                      <SectionLabel>Special Assistance Notes</SectionLabel>
                    </SectionTitle>
                    <Controller
                      name="deliveryAddress.specialAssistanceNotes"
                      control={control}
                      shouldUnregister
                      render={({ field: notesField }) => (
                        <StyledTextField
                          {...notesField}
                          fullWidth
                          multiline
                          rows={7}
                          label="Special Assistance Notes (Optional)"
                          size="small"
                          error={!!errors?.deliveryAddress?.specialNotes}
                          helperText={
                            errors?.deliveryAddress?.specialNotes?.message
                          }
                        />
                      )}
                    />
                  </>
                )}
              </>
            )}
          />
        </AssistanceBox>

        {/* DELIVERY TIME SLOT */}
        <SectionTitle>
          <IconWrapper>
            <DescriptionIcon />
          </IconWrapper>
          <SectionLabel>Delivery Time Slot</SectionLabel>
        </SectionTitle>
        <FormGrid>
          <Controller
            name="deliveryAddress.startDate"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                size="small"
                inputProps={{
                  min: today,
                }}
                error={!!errors?.deliveryAddress?.startDate}
                helperText={errors?.deliveryAddress?.startDate?.message}
              />
            )}
          />
          <Controller
            name="deliveryAddress.endDate"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                fullWidth
                label="End Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                size="small"
                inputProps={{
                  min: deliveryStartDate || today,
                }}
                error={!!errors?.deliveryAddress?.endDate}
                helperText={errors?.deliveryAddress?.endDate?.message}
              />
            )}
          />
          <Controller
            name="deliveryAddress.deliveryTimeSlot"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                select
                fullWidth
                label="Delivery Time Slot"
                SelectProps={{ native: true }}
                size="small"
                error={!!errors?.deliveryAddress?.timeSlot}
                helperText={errors?.deliveryAddress?.timeSlot?.message}
              >
                <option value="">Select time slot</option>
                <option value="08:00-10:00">08:00 AM - 10:00 AM</option>
                <option value="10:00-12:00">10:00 AM - 12:00 PM</option>
                <option value="12:00-14:00">12:00 PM - 2:00 PM</option>
                <option value="14:00-16:00">2:00 PM - 4:00 PM</option>
                <option value="16:00-18:00">4:00 PM - 6:00 PM</option>
              </StyledTextField>
            )}
          />
        </FormGrid>

        {/* NOTES */}
        <SectionTitle>
          <IconWrapper>
            <NotesIcon />
          </IconWrapper>
          <SectionLabel>Delivery Notes</SectionLabel>
        </SectionTitle>

        <NotesBox>
          <Box
            sx={{
              padding: "16px",
              borderRadius: "12px",
              backgroundColor: "rgba(255,193,7,0.08)",
              border: "1px solid rgba(255,193,7,0.3)",
              display: "flex",
              gap: "12px",
              alignItems: "flex-start",
              mb: 5,
            }}
          >
            {/* ICON */}
            <Box
              sx={{
                width: "28px",
                height: "28px",
                minWidth: "28px",
                borderRadius: "6px",
                backgroundColor: "rgba(255,193,7,0.25)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: "2px",
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 20, color: "#b7791f" }} />
            </Box>

            {/* TEXT CONTENT */}
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "15px",
                  mb: 0.5,
                  color: "#8a6d3b",
                }}
              >
                Delivery Notes — What to include
              </Typography>

              {/* 2-COLUMN LIST */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  rowGap: "8px",
                  columnGap: "16px",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Unloading restrictions
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Location access details
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Preferred delivery timings
                </Typography>
              </Box>
            </Box>
          </Box>

          <Controller
            name="deliveryAddress.deliveryNotes"
            control={control}
            render={({ field: notesField }) => (
              <StyledTextField
                {...notesField}
                fullWidth
                multiline
                rows={7}
                label="Delivery Notes (Important) *"
                size="small"
                error={!!errors?.deliveryAddress?.deliveryNotes}
                helperText={errors?.deliveryAddress?.deliveryNotes?.message}
              />
            )}
          />
        </NotesBox>

        {/* FILE UPLOADS */}
        <SectionTitle>
          <IconWrapper>
            <NotesIcon />
          </IconWrapper>
          <SectionLabel>Delivery Images/Documents</SectionLabel>
        </SectionTitle>

        <FullWidthGrid
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <Box
            sx={{
              border: "2px dashed #e5e7eb",
              borderRadius: 1,
              p: 2,
              textAlign: "center",
              backgroundColor: "#f9fafb",
            }}
          >
            <CloudUpload sx={{ fontSize: 32, color: "#9ca3af", mb: 1 }} />
            <Typography variant="body2">
              Drag files or click to upload
            </Typography>
          </Box>

          <SectionLabel>Documents Upload</SectionLabel>
          <Box
            sx={{
              border: "2px dashed #e5e7eb",
              borderRadius: 1,
              p: 2,
              textAlign: "center",
              backgroundColor: "#f9fafb",
            }}
          >
            <CloudUpload sx={{ fontSize: 32, color: "#9ca3af", mb: 1 }} />
            <Typography variant="body2">
              Drag files or click to upload
            </Typography>
          </Box>
        </FullWidthGrid>
      </StyledPaper>

      {/* Transport & Delivery Documents */}
      <StyledPaper elevation={0} style={{ marginTop: 10 }}>
        <HeadingBox>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: "30px",
              color: "#0a0e27",
              background: "linear-gradient(135deg, #0a0e27 0%, #1976d2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1,
            }}
          >
            Transport & Delivery Documents
          </Typography>

          <Typography sx={{ color: "#718096", fontSize: "15px" }}>
            Upload the required documents for third-party delivery
          </Typography>
        </HeadingBox>

        <FullWidthGrid>
          {documents.map((doc, idx) => (
            <Box key={doc.name}>
              <Controller
                name={`transportDocuments.${doc.name}`}
                control={control}
                render={({ field }) => (
                  <StyledTextField
                    fullWidth
                    label={doc.label}
                    value={
                      // show filename from RHF OR local state
                      field.value?.name ||
                      transport?.documents?.[doc.name]?.[0]?.name ||
                      ""
                    }
                    placeholder={`Upload ${doc.label}`}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors?.transportDocuments?.[doc.name]}
                    helperText={errors?.transportDocuments?.[doc.name]?.message}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Button
                          variant="contained"
                          component="label"
                          sx={{ ml: 1, px: 8, py: 1.5, borderRadius: "8px" }}
                        >
                          Upload
                          <input
                            type="file"
                            hidden
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (!file) return;

                              if (file.type !== "application/pdf") {
                                alert("Please upload a valid PDF file.");
                                return;
                              }

                              field.onChange(file); //  RHF value
                              handleFileUpload(doc.name)(file); //  UI state only
                            }}
                          />
                        </Button>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          ))}
        </FullWidthGrid>

        <SectionTitle>
          <IconWrapper>
            <NotesIcon />
          </IconWrapper>
          <SectionLabel>Tracking Details</SectionLabel>
        </SectionTitle>
        <NotesBox>
          <Box
            sx={{
              padding: "16px",
              borderRadius: "12px",
              backgroundColor: "rgba(255,193,7,0.08)",
              border: "1px solid rgba(255,193,7,0.3)",
              display: "flex",
              gap: "12px",
              alignItems: "flex-start",
              mb: 5,
              mt: 2,
            }}
          >
            {/* ICON BOX */}
            <Box
              sx={{
                width: "28px",
                height: "28px",
                borderRadius: "6px",
                backgroundColor: "rgba(255,193,7,0.25)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: "2px",
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 20, color: "#b7791f" }} />
            </Box>

            {/* TEXT CONTENT */}
            <Box sx={{ width: "100%" }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "15px",
                  mb: 1,
                  color: "#8a6d3b",
                }}
              >
                Tracking Details — What to include
              </Typography>

              {/* 2 COLUMN BULLET GRID */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  rowGap: "8px",
                  columnGap: "16px",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Transporter name & contact number
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Vehicle / container number
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Driver name & phone number
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Dispatch time & expected delivery time
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Tracking link or LR / reference number
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Latest shipment status or movement updates
                </Typography>
              </Box>
            </Box>
          </Box>
          <Controller
            name="transportDocuments.trackingNotes"
            control={control}
            render={({ field: notesField }) => (
              <StyledTextField
                {...notesField}
                fullWidth
                multiline
                rows={7}
                label="Tracking Notes (Important) *"
                size="small"
              />
            )}
          />
        </NotesBox>
      </StyledPaper>

      {/* BILLING ADDRESS */}
      <StyledPaper elevation={0} style={{ marginTop: 10 }}>
        <Box>
          <HeadingBox>
            <Typography
              variant="h4"
              sx={{
                fontWeight: "800",
                fontSize: "30px",
                background: "linear-gradient(135deg, #0a0e27 0%, #1976d2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                marginBottom: "12px",
              }}
            >
              Billing Address
            </Typography>

            <Typography
              sx={{ color: "#718096", fontSize: "15px", fontWeight: 500 }}
            >
              Required for every order
            </Typography>
          </HeadingBox>

          <SectionTitle>
            <IconWrapper>
              <PersonIcon />
            </IconWrapper>
            <SectionLabel>Contact Information</SectionLabel>
          </SectionTitle>
          <FormGrid>
            <Controller
              name="billingAddress.firstName"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="First Name *"
                  size="small"
                  error={!!errors?.billingAddress?.firstName}
                  helperText={errors?.billingAddress?.firstName?.message}
                />
              )}
            />

            <Controller
              name="billingAddress.lastName"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Last Name *"
                  size="small"
                  error={!!errors?.billingAddress?.lastName}
                  helperText={errors?.billingAddress?.lastName?.message}
                />
              )}
            />

            <Controller
              name="billingAddress.phone"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Phone *"
                  size="small"
                  error={!!errors?.billingAddress?.phone}
                  helperText={errors?.billingAddress?.phone?.message}
                />
              )}
            />

            <Controller
              name="billingAddress.email"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Email *"
                  type="email"
                  size="small"
                  error={!!errors?.billingAddress?.email}
                  helperText={errors?.billingAddress?.email?.message}
                />
              )}
            />
            <Controller
              name="billingAddress.companyName"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Company Name *"
                  size="small"
                  error={!!errors?.billingAddress?.companyName}
                  helperText={errors?.billingAddress?.companyName?.message}
                />
              )}
            />
            <Controller
              name="billingAddress.taxId"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="ABN/ACN/VAT/Tax ID *"
                  size="small"
                  error={!!errors?.billingAddress?.taxId}
                  helperText={errors?.billingAddress?.taxId?.message}
                />
              )}
            />
          </FormGrid>

          <SectionTitle>
            <IconWrapper>
              <LocationOnIcon />
            </IconWrapper>
            <SectionLabel>Location Details</SectionLabel>
          </SectionTitle>

          <FormGrid>
            <Controller
              name="billingAddress.country"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  select
                  fullWidth
                  label="Country *"
                  size="small"
                  error={!!errors?.billingAddress?.country}
                  helperText={errors?.billingAddress?.country?.message}
                >
                  <MenuItem value="">Select country</MenuItem>
                  {countries.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </StyledTextField>
              )}
            />

            <Controller
              name="billingAddress.state"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  select
                  fullWidth
                  label="State *"
                  size="small"
                  error={!!errors?.billingAddress?.state}
                  helperText={errors?.billingAddress?.state?.message}
                >
                  <MenuItem value="">Select state</MenuItem>
                  {billing.states.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </StyledTextField>
              )}
            />

            <Controller
              name="billingAddress.city"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  select
                  fullWidth
                  label="City *"
                  size="small"
                  error={!!errors?.billingAddress?.city}
                  helperText={errors?.billingAddress?.city?.message}
                >
                  <MenuItem value="">Select city</MenuItem>
                  {billing.cities.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </StyledTextField>
              )}
            />

            <Controller
              name="billingAddress.postcode"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Postcode *"
                  size="small"
                  error={!!errors?.billingAddress?.postcode}
                  helperText={errors?.billingAddress?.postcode?.message}
                />
              )}
            />
          </FormGrid>

          <SectionTitle>
            <IconWrapper>
              <HomeIcon />
            </IconWrapper>
            <SectionLabel>Billing Address</SectionLabel>
          </SectionTitle>
          <FormGrid>
            <Controller
              name="billingAddress.addressLine1"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Address Line 1 *"
                  size="small"
                  error={!!errors?.billingAddress?.addressLine1}
                  helperText={errors?.billingAddress?.addressLine1?.message}
                />
              )}
            />

            <Controller
              name="billingAddress.addressLine2"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Address Line 2"
                  size="small"
                  error={!!errors?.billingAddress?.addressLine2}
                  helperText={errors?.billingAddress?.addressLine2?.message}
                />
              )}
            />
            <Controller
              name="billingAddress.landmark"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  fullWidth
                  label="Landmark"
                  size="small"
                  error={!!errors?.billingAddress?.landMark}
                  helperText={errors?.billingAddress?.landMark?.message}
                />
              )}
            />
          </FormGrid>
        </Box>
      </StyledPaper>
    </>
  );
}
